import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  IconButton,
  Fade,
  Paper,
  Stack,
  Divider,
  Chip,
  Grid
} from '@mui/material';
import {
  Mic,
  MicOff,
  SmartToy,
  Send,
  VolumeUp,
  History,
  Psychology,
  AutoAwesome,
  KeyboardArrowRight,
  Person
} from '@mui/icons-material';
import { useVoice } from '../contexts/VoiceContext';

const Asistente = () => {
  const { 
    isListening, 
    startListening, 
    stopListening, 
    transcript, 
    speak,
    fullTranscript 
  } = useVoice();
  
  const [messages, setMessages] = useState([
    { type: 'ai', text: 'Kaixo! Soy el motor cognitivo BASERRI-ADITU. Registro de voz optimizado y activo. ¿Qué instrucción desea ejecutar?' }
  ]);

  const handleToggle = () => {
    if (isListening) {
      stopListening();
      if (fullTranscript) process(fullTranscript);
    } else {
      startListening();
    }
  };

  const process = (txt) => {
    setMessages(prev => [...prev, { type: 'user', text: txt }]);
    setTimeout(() => {
      const resp = 'Procesando comando técnico... Sincronización con base de datos REGA completada. Trámite en curso.';
      setMessages(prev => [...prev, { type: 'ai', text: resp }]);
      speak(resp);
    }, 800);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: 8, minHeight: '100vh', bgcolor: 'var(--bg-app)' }}>
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        {/* Main Voice Portal Card */}
        <Card className="premium-card accent-emerald" sx={{ mb: 4, overflow: 'hidden' }}>
          <Box sx={{ p: 4, textAlign: 'center', position: 'relative', bgcolor: 'var(--navy-main)', color: 'white' }}>
            <Avatar sx={{ bgcolor: 'var(--emerald-main)', width: 120, height: 120, mx: 'auto', mb: 2, boxShadow: '0 0 40px rgba(16, 185, 129, 0.4)' }}>
              <SmartToy sx={{ fontSize: 60 }} />
            </Avatar>
            <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>ASISTENTE COGNITIVO</Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.8, fontWeight: 500 }}>Control por Voz e Inteligencia Operativa</Typography>
            
            {/* Listening Animation Overlay */}
            {isListening && (
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(5, 150, 105, 0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                {[...Array(8)].map((_, i) => (
                  <Box key={i} sx={{ width: 6, height: 40, bgcolor: 'white', borderRadius: 4, animation: 'wave 1s infinite', animationDelay: `${i * 0.1}s` }} />
                ))}
              </Box>
            )}
          </Box>
          
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" sx={{ color: 'var(--slate-light)', mb: 4, fontWeight: 500 }}>
              {isListening ? 'Sincronizando flujo de audio en tiempo real...' : 'Presione para iniciar la comunicación bidireccional.'}
            </Typography>
            
            <IconButton 
              onClick={handleToggle}
              sx={{ 
                width: 100, height: 100, 
                bgcolor: isListening ? 'var(--alert-crimson)' : 'var(--emerald-main)', 
                color: 'white',
                boxShadow: 'var(--shadow-md)',
                '&:hover': { bgcolor: isListening ? '#b91c1c' : 'var(--forest-main)' }
              }}
            >
              {isListening ? <MicOff sx={{ fontSize: 40 }} /> : <Mic sx={{ fontSize: 40 }} />}
            </IconButton>

            {transcript && (
              <Fade in>
                <Paper sx={{ mt: 4, p: 3, bgcolor: 'var(--slate-bg)', border: '1px dashed var(--border-color)', borderRadius: '16px' }}>
                  <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'var(--navy-main)', fontWeight: 700 }}>"{transcript}"</Typography>
                </Paper>
              </Fade>
            )}
          </CardContent>
        </Card>

        {/* Console Log */}
        <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--navy-main)', mb: 2 }}>Registro de Interacción</Typography>
        <Stack spacing={2}>
          {messages.map((m, i) => (
            <Fade in key={i}>
              <Box sx={{ display: 'flex', justifyContent: m.type === 'ai' ? 'flex-start' : 'flex-end', gap: 2 }}>
                <Paper sx={{ 
                  p: 2, px: 3, maxWidth: '80%', 
                  borderRadius: '20px',
                  bgcolor: m.type === 'ai' ? 'white' : 'var(--navy-main)',
                  color: m.type === 'ai' ? 'var(--navy-main)' : 'white',
                  border: m.type === 'ai' ? '1px solid var(--border-color)' : 'none',
                  boxShadow: 'var(--shadow-sm)'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    {m.type === 'ai' ? <SmartToy fontSize="small" /> : <Person fontSize="small" />}
                    <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase' }}>{m.type === 'ai' ? 'Sistema / ADITU' : 'Operador'}</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.6 }}>{m.text}</Typography>
                </Paper>
              </Box>
            </Fade>
          ))}
        </Stack>

        {/* Suggestion Grid */}
        <Grid container spacing={2} sx={{ mt: 4 }}>
          {[
            { title: 'Generar Guía', desc: 'Trámite de transporte oficial' },
            { title: 'Censo Real', desc: 'Consulta de existencias REGA' },
            { title: 'Alerta Salud', desc: 'Reportar incidencia sanitaria' }
          ].map((s, i) => (
            <Grid item xs={12} md={4} key={i}>
              <Paper sx={{ p: 2, borderRadius: '16px', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { bgcolor: 'white', transform: 'translateY(-4px)', boxShadow: 'var(--shadow-md)' } }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'var(--navy-main)' }}>{s.title}</Typography>
                <Typography variant="caption" sx={{ color: 'var(--slate-light)' }}>{s.desc}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default Asistente;
